// One place where each configurator declares its OWN behaviour.
//
// WHY THIS EXISTS
// cpq2 started as a single configurator, so anything product-specific was a
// module-level constant: a set of question codes, a set of group codes, an
// unconditional call in the submit path. That was correct while there was one
// product. It stopped being correct the moment there were two, because a question
// CODE is only unique WITHIN a configurator -- "CORE_TYPE" is a real, answerable
// field in Flame Cells and a deliberately hidden one in Natural Draft Burners, and
// a flat set of codes cannot tell those apart. Hiding it once hid it in both, which
// also took out CORE_PLUG_TYPE and CORE_PROTRUDING, whose hide rule can only be
// satisfied by answering the question that was hidden.
//
// Six codes appear in BOTH exports today (CELL_OD, CORE_TYPE, DESCRIPTION,
// FLAME_CELL, GAS_TYPE, UNIQUE2), so this is not a hypothetical.
//
// THE RULE: shared machinery may not carry product knowledge. It asks the profile.
// Adding a configurator means adding a profile, not editing a set somewhere.
//
// Being explicit also matters for the negatives. A configurator that declares
// serialTag: false is saying something the code can rely on -- not "nobody wired it
// up yet", which is how a Flame Cell config nearly ended up with a Natural Draft
// serial tag line: the resolver never fails, it falls back to a default tag, so an
// ungated call produced a WRONG tag rather than no tag.

import type { ComputedRoutingOp } from "./flameCellRouting";
import type { BurnerModel } from "./ndbBurnerModel";
import type { AnswerState } from "../types";

export type SubmitSteps = {
  // Add the customer's Serial Tag as its own sales-order line (util/serialTag.ts).
  serialTag: boolean;
  // Drag extra item lines onto the order -- Flange Gasket / Ext Nipple / Main
  // Burner / Nozzle (util/additionalLines.ts).
  additionalLines: boolean;
  // Look the configuration up in a price list and set the item price + line rate
  // (util/ndbPricing.ts). False leaves the CONFIGURED_LINE_AMOUNT placeholder.
  listPricing: boolean;
  // Create the customrecord_customer_requirements record
  // (util/customerRequirements.ts). This one WRITES TO NETSUITE, so leaving it on
  // for a product whose questions it does not understand creates a real, mostly
  // empty record against a live customer.
  customerRequirements: boolean;
  // Write the Natural Draft item fields cpq2 sets directly rather than through the
  // encoded mapping: custiteminlet_connection_notes, custitem_extrainletconnections,
  // custitemflamecell_coretype, custitem_hoodsighttube, custitem_burnerbelowcenter.
  // Their value maps are keyed by NDB ANSWER CODES, so they cannot be reused by a
  // product whose answers are different even where the question code matches.
  ndbItemFields: boolean;
};

/** One cpq2-owned hide rule for a set of ANSWER OPTIONS.
 *
 *  The export gives every answer a `parsed_hide_rule` and the whole app already honours it
 *  -- the widget filters the option out, validation stops counting the question, nothing has
 *  to be told twice. These entries are merged into that same field at load time, so a rule
 *  cpq2 owns behaves exactly like one NetSuite authored.
 *
 *  DECLARED HERE RATHER THAN EDITED INTO questions.json because
 *  scripts/extract-product.cjs rewrites that folder wholesale on the next import, and the
 *  edit would be silently lost -- the same reason hiddenGroupCodesByTab exists. */
export type AnswerHideRule = {
  /** Question whose options the rule applies to. */
  question: string;
  /** The option codes it applies to. */
  answers: readonly string[];
  /** Apply to every OTHER option of that question instead of the ones named. Saves listing
   *  twelve codes to say "only these four", and a new option added in NetSuite is then
   *  covered automatically rather than quietly escaping the rule. */
  otherAnswers?: boolean;
  /** Parsed-rule expression (see util/rule.ts). The options are HIDDEN while it is true.
   *  Composed with the export's own rule by OR, so neither can override the other. */
  hiddenWhen: string;
};

export type ProductProfile = {
  /** products.json slug. Must match the folder under /data and the snapshot stamp. */
  slug: string;

  // ---- UI: questions and groups kept off this configurator ------------------
  /** Questions hidden from the UI even though the export gives them no hide rule. */
  hiddenQuestionCodes: ReadonlySet<string>;
  /** Groups suppressed per tab code, regardless of their own hide rules. */
  hiddenGroupCodesByTab: Readonly<Record<string, ReadonlySet<string>>>;
  /** Answer options this configurator hides beyond what its export says; see AnswerHideRule.
   *  Applied by util/answerHideRules.ts when the product loads. */
  answerHideRules: ReadonlyArray<AnswerHideRule>;
  /** Answer options the export hides OUTRIGHT (hide rule the always-hide rule (star, slash, star), parsed "true") that this
   *  configurator offers anyway. The one direction answerHideRules cannot go. Only an
   *  unconditional hide is lifted: an option the export hides under a condition keeps that
   *  condition. Applied by util/answerHideRules.ts, before the hide rules, when the product
   *  loads. */
  answerReveals?: ReadonlyArray<{ question: string; answers: readonly string[] }>;
  /** Whole questions this configurator hides under a condition of its own, beyond what its
   *  export says. The sibling of answerHideRules one level up; applied by
   *  util/questionHideRules.ts when the product loads.
   *
   *  A hidden question KEEPS ITS ANSWER, so a rule that matters is always paired with an
   *  operation that forces the value -- hiding stops it being picked, the operation stops a
   *  stale one surviving into the item, the BOM or the key. */
  questionHideRules?: ReadonlyArray<{
    /** The question codes to hide. */
    questions: readonly string[];
    /** Parsed-rule expression; the questions are HIDDEN while it is true. Composed with the
     *  export's own rule by OR, so this can only ever hide more, never reveal. */
    hiddenWhen: string;
  }>;
  /** Whole questions this configurator LOCKS under a condition of its own: shown with their
   *  value, not editable. For a rule with one answer that should still be seen and still reach
   *  the item -- a hidden question encodes as blank in the mapping, a locked one keeps its
   *  value. Always paired with an operation that sets that value. Applied by
   *  util/questionHideRules.ts, composed with the export's read-only rule by OR. */
  questionLockRules?: ReadonlyArray<{
    questions: readonly string[];
    lockedWhen: string;
  }>;

  // ---- Validation ----------------------------------------------------------
  /** Visible fields the user may always leave blank.
   *
   *  Read by validation (not counted as missing) AND by the field label, which says
   *  "(optional)" -- one set, so a field cannot be labelled optional and still block the
   *  submit, or block it with nothing on screen saying so. */
  optionalQuestionCodes: ReadonlySet<string>;
  /**
   * Optional-as-a-PAIR: two fields a config may simply not have, where neither has a
   * None/0 "not used" option. The pair counts as satisfied only while BOTH are blank;
   * once one is set the other becomes required, since half a pair would flow into the
   * mapping/description incomplete.
   */
  optionalQuestionPairs: ReadonlyArray<readonly [string, string]>;
  /** Fields blank-is-legal on the record but NOT acceptable here. */
  hardRequiredQuestionCodes: ReadonlySet<string>;

  // ---- Widgets -------------------------------------------------------------
  /** Numeric fields whose typed value gets its commas / M-MM suffix normalised. */
  suffixNormalizedFields: ReadonlySet<string>;
  /** Group codes treated as the FAH-collapsible sections. */
  fahSectionGroupCodes: ReadonlySet<string>;
  /** Dropdown questions rendered as a TYPEABLE combo box: pick from the list, or type a
   *  size and have it resolve onto the matching option. Declared per product because a
   *  question code is only unique within one CPQ. */
  comboBoxQuestionCodes: ReadonlySet<string>;
  /** Combo-box questions where a value in NO list is CREATED rather than refused: added to
   *  its NetSuite custom list, recorded in cpq2_custom_answers, and offered to everyone
   *  afterwards. Separate from comboBoxQuestionCodes because typing and creating are
   *  different powers - a question can be typeable without being extendable. Only ever a
   *  subset; a code here that is not a combo box does nothing. */
  createsAnswersFor: ReadonlySet<string>;
  /** Of those, the ones that create WITHOUT asking first (Kevin, 2026-08-20, for Hood Size:
   *  "it should be able to update its netsuite list automatically without the user having to
   *  click add").
   *
   *  Not the default, and the difference is not cosmetic: a NetSuite list value can be
   *  inactivated but never deleted, so a typo is permanent clutter. Confirming is right where
   *  a typo is PLAUSIBLE -- Flange to Nozzle End takes sixteenths, so `19-3/4` and `19-3/16`
   *  are one keystroke apart. It is pointless where the grammar already rejects near misses:
   *  pair this with wholeNumberQuestionCodes and the only thing that can be created is an
   *  integer number of inches. */
  autoCreatesAnswersFor: ReadonlySet<string>;
  /** Combo-box questions that accept WHOLE NUMBERS only, so fractions and decimals are
   *  refused rather than normalised. What makes auto-creation safe: it narrows the values
   *  that can reach the list to ones that are hard to typo into something plausible. */
  wholeNumberQuestionCodes: ReadonlySet<string>;
  /** Combo-box questions where a well-formed size that is in NO list is kept as FREE TEXT in
   *  ANOTHER question, instead of becoming a new NetSuite list value. Maps the combo question
   *  code -> [text question, text answer] to hold the typed size.
   *
   *  The third possible answer to "a size that is not in the list", after refuse
   *  (comboBoxQuestionCodes alone) and create (createsAnswersFor) - and the only one that
   *  works for a question whose list has run out of usable internal ids. The token in a
   *  2-character mapping slot IS the NetSuite internal id, so a list that has passed id 99
   *  can never contribute another encodable value; the text question's own slot is 8
   *  characters of mixed inches and has no such ceiling.
   *
   *  Two rules make this safe, and both are enforced in ComboWidget rather than here:
   *    - a size that IS listed still selects the list option, so nothing about an existing
   *      configuration changes; and
   *    - choosing a listed size CLEARS the text question again, so the two can never both
   *      hold a value. That matters beyond tidiness: the text question feeds the fingerprint,
   *      so a stale value left behind would move the part number of a configuration that had
   *      not changed - and a moved fingerprint means a DUPLICATE item. */
  unlistedSizeTextFallback: ReadonlyMap<string, readonly [string, string]>;
  /** Typed size fields with a CEILING, in inches: "QUESTION/ANSWER" key -> the largest
   *  value allowed. Anything above it is flagged on the field and hard-blocks Save and
   *  Submit (see util/sizeLimits). Fractions and decimals are read the way the part
   *  number reads them, so "99-15/16" and "99.94" are both over a 99.99 cap or both not. */
  maxInches: ReadonlyMap<string, number>;

  // ---- Mapping encoder -----------------------------------------------------
  /**
   * User-input fields that KEEP their value in the encoded mapping even when the
   * group/tab rendering them is hidden. Every other group/tab-hidden mapped field
   * goes to its none token.
   */
  mappingHideExemptCodes: ReadonlySet<string>;

  // ---- Save and Submit -----------------------------------------------------
  submit: SubmitSteps;

  /**
   * The "QUESTION/ANSWER" answer key holding this configuration's fingerprint - the
   * generated id behind the part number.
   *
   * It MUST equal this product's ITCR itemid template (custrecord_cpqc_itcr_itemid)
   * with the braces stripped, because api/create-assembly stores
   * custitem_fingerprint = item.itemid, and findItemByFingerprint matches on that
   * field. Read the wrong key and the fingerprint comes back EMPTY, the
   * already-exists lookup is skipped, and every submit builds a NEW item for a
   * configuration that already has one. test:products asserts the two agree.
   *
   * The two products genuinely differ: Natural Draft asks HIDDEN_ID_KEY/ID_KEY,
   * Flame Cells asks ID_KEY/ID.
   */
  fingerprintAnswerKey: string;

  /**
   * How this configurator gets its manufacturing routing.
   *
   * "rulesets" -- the default -- filters routings.json by each row's ROUTING_RULE, which
   * is what Natural Draft does (4002 rows).
   *
   * A FUNCTION means the routing is COMPUTED from the item fields the ITCR just built,
   * because the product has no routing rulesets to filter. Flame Cells is the first: its
   * routing came from a NetSuite User Event script driven by a labor model (rolling time
   * as a function of die, material and OD), which no ruleset can express, and
   * routings.json is empty for it.
   *
   * Returning ok:false is a real answer, not an error -- "this configuration should not
   * get a routing" -- and the reason is surfaced as a build warning naming what was
   * missing, instead of the generic "No routing operations matched ROUTING_RULE".
   */
  /**
   * False when this configurator has NO manufacturing routing at all.
   *
   * Per Kevin (2026-08-12), "flange will not have a route". Its ITCRs say otherwise -
   * both set routing:true - so without this the missing-routing warning blocked every
   * Flange submit over something the product does not have. buildAssembly ANDs this with
   * the ITCR flag, so either source can say no and neither can force a routing on.
   *
   * Declared here rather than waiting on the ITCR flag being corrected in NetSuite,
   * because the product fact is known now. If the flag is later set to false the two
   * simply agree; if Flange ever gains a real routing, remove this and the ITCR takes
   * over.
   */
  buildsRouting?: boolean;

  /**
   * BOM components to use when this product's materials ruleset yields NONE.
   *
   * Every other configurator's export carries a single unconditional material row
   * pointing at the shared CPQ placeholder item (548487) -- that is what their BOMs
   * actually contain, since none of them drives components from the configuration yet.
   * Flange's export has no material rows at all, so its BOM came out empty and the
   * missing-components warning blocked every submit.
   *
   * A FALLBACK rather than an addition, on purpose: the moment Flange's export grows the
   * material row this stands in for, the ruleset wins and this stops being used, so it
   * cannot double up the placeholder.
   */
  bomFallbackComponents?: ReadonlyArray<{ item: string; qty: string }>;

  /**
   * TWO-LEVEL BOM: when this rule is true, the item's BOM is a CHILD ASSEMBLY plus whatever
   * material rows are declared in `nestedKeepRowPrefixes` -- not the full material list.
   *
   * Flame Cells, Kevin 2026-09-08: "a flame cell with a face plate will always have a flame
   * cell with no face plate in its bom."  The plated cell is a two-line assembly (the bare
   * cell, and the FP-* face plate); the fin stock, core, plug and rivets belong to the bare
   * cell one level down.  855 of 1000 sampled BOM lines in the account are built that way.
   *
   * The builder CANNOT resolve the child: that means re-running the operation pipeline with
   * the face plate set to None and then finding-or-creating the resulting item in NetSuite,
   * neither of which a pure function can do.  So it emits a component whose item is
   * `NESTED_CHILD_ITEM` and sets `needsNestedChild` on the preview; the caller builds the
   * child, creates it, and substitutes its internal id before writing the revision.
   *
   * ORDER MATTERS AND IS NOT MERELY TIDY: the child must exist before the parent's revision
   * is written.  BOM idempotency is "create only when the item has no BOM attached", so a
   * parent whose BOM was written while the child was still unresolved keeps a short
   * component list forever -- every later run sees a BOM and skips it.
   */
  nestedChildWhen?: string;

  /**
   * Material row prefixes that STAY on the parent's BOM when `nestedChildWhen` fires.
   * Everything else moves down to the child.  For Flame Cells this is the face plate row:
   * the plate is fitted to the plated assembly, not to the bare cell inside it.
   */
  nestedKeepRowPrefixes?: ReadonlyArray<string>;

  /**
   * The answer overlay that turns a parent configuration into its CHILD -- for Flame Cells,
   * setting the face plate size to None.  Applied over the parent's fully resolved answers
   * and re-run through the operation pipeline, which is what collapses the size/peep/features
   * triple back onto CELL_FACE_NONE and rebuilds the key's fourth segment.
   *
   * Every option of the overlaid question is listed, not just the one being selected: an
   * answer map holds one key per option, so setting only the wanted one would leave the old
   * selection standing alongside it.
   */
  nestedChildOverlay?: Readonly<AnswerState>;

  /**
   * Narrow the cell options of a TABLE column (a type-11 question).
   *
   * The options live in the column answer's `custrecord_cpqc_answer_qc_opt`, which
   * answerHideRules cannot reach -- its answers ARE the columns, so hiding one hides the whole
   * column rather than a value inside it.
   *
   * `keep` lists the options that may be offered. Anything else the export carries is dropped
   * from the picker; "None" is always kept when the column already had it. NARROWS ONLY --
   * naming an option the column does not have adds nothing, so a rule can restrict what
   * NetSuite offers and never invent a value the MAPPING encoder has no token for.
   *
   * Omit `columns` to narrow every column of that question.
   */
  tableCellOptions?: ReadonlyArray<{
    question: string;
    columns?: readonly string[];
    keep: readonly string[];
  }>;

  /**
   * Component families this configurator EXPECTS on a finished BOM, so that a part the
   * configuration calls for but has no item mapped for is caught rather than silently
   * absent (Kevin, 2026-08-21).
   *
   * `requiredWhen` is a parsed rule over the answers — true means "this configuration needs
   * one of these". `rowPrefixes` are the material-row name prefixes that satisfy it. A
   * family that is required but produced no component line is REPORTED, and the BOM name is
   * prefixed with "!" so it is obvious in NetSuite that the BOM is incomplete.
   *
   * Matching on the row NAME rather than on item ids keeps the two in one place: the rows
   * are named "Fin Stock - …", "Core Plug - …" and so on, so adding a row to a family needs
   * no second edit here.
   */
  expectedComponentFamilies?: ReadonlyArray<{
    /** How the family is named in a warning or an email. */
    label: string;
    /** Material-row name prefixes that satisfy this family. */
    rowPrefixes: readonly string[];
    /** Parsed rule; true = this configuration needs one of these. */
    requiredWhen: string;
  }>;

  computeRouting?: (args: { itemid: string; fields: Record<string, string> }) =>
    | { ok: true; operations: ComputedRoutingOp[]; subsidiaryId: string; locationId: string; detail: string }
    | { ok: false; reason: string };

  /**
   * Sales-order BODY fields this configurator writes, as [answer key, field id].
   * The write-back merges them onto the order; see util/orderFields.ts.
   */
  orderFields: ReadonlyArray<readonly [string, string]>;

  /**
   * The answers as a Sales Burner Configurator burner (products/ndbBurnerModel.ts), or null
   * until there is one to draw. Declaring it does two things: the live 3D model replaces the
   * product illustration (components/BurnerModelPanel), and Save and Submit attaches the
   * customer drawing + STEP model to the item (api/customer-files). Absent = neither.
   */
  burnerModel?: (answers: AnswerState) => BurnerModel | null;
};

const EMPTY_SET: ReadonlySet<string> = new Set();
const EMPTY_MAP: ReadonlyMap<string, readonly [string, string]> = new Map();

/**
 * The profile for a configurator that is not in the registry.
 *
 * Every list is empty and every submit step is OFF, which is the only safe default:
 * an unknown product must not inherit another product's questions, validation, or
 * NetSuite writes. It degrades to "render exactly what the export says, add one
 * line to the order", never to Natural Draft behaviour.
 */
export const NEUTRAL_PROFILE: ProductProfile = {
  slug: "",
  hiddenQuestionCodes: EMPTY_SET,
  hiddenGroupCodesByTab: {},
  answerHideRules: [],
  optionalQuestionCodes: EMPTY_SET,
  optionalQuestionPairs: [],
  hardRequiredQuestionCodes: EMPTY_SET,
  suffixNormalizedFields: EMPTY_SET,
  fahSectionGroupCodes: EMPTY_SET,
  comboBoxQuestionCodes: EMPTY_SET,
  createsAnswersFor: EMPTY_SET,
  autoCreatesAnswersFor: EMPTY_SET,
  wholeNumberQuestionCodes: EMPTY_SET,
  unlistedSizeTextFallback: EMPTY_MAP,
  maxInches: new Map(),
  mappingHideExemptCodes: EMPTY_SET,
  // No key: an unknown product has no fingerprint, so the already-exists lookup is
  // skipped rather than run against another product's key.
  fingerprintAnswerKey: "",
  submit: {
    serialTag: false,
    additionalLines: false,
    listPricing: false,
    customerRequirements: false,
    ndbItemFields: false,
  },
  orderFields: [],
};
